/**
 * Market-regime direction lookbacks (v3) — FE mirror of
 * `tessera-dashboard-backend/src/configs/market_regime_config.py`
 * (`direction_lookbacks_for_tf` / `_DIRECTION_LOOKBACKS_BY_TF`).
 *
 * Labels for long/short horizon must use these bar counts for the report candle TF.
 * Unknown TFs fall back to 120 / 480 (same as 1m/5m).
 */

export const MARKET_REGIME_DIRECTION_DEADBAND_PCT = 0.05;

/** (shortBars, longBars) on the candle TF — official v3 product path. */
const DIRECTION_LOOKBACKS_BY_TF: Record<string, readonly [number, number]> = {
  "1m": [120, 480],
  "5m": [120, 480],
  "15m": [96, 288],
  "30m": [64, 192],
  "1h": [48, 120],
  "4h": [30, 60],
  "1d": [15, 45],
  d: [15, 45],
  day: [15, 45],
};

const TF_ALIASES: Record<string, string> = {
  m1: "1m",
  m5: "5m",
  m15: "15m",
  m30: "30m",
  h1: "1h",
  "60m": "1h",
  h4: "4h",
  "240m": "4h",
  d1: "1d",
  "1day": "1d",
};

export type DirectionHorizon = "short" | "long";

export type DirectionLookbacks = {
  shortBars: number;
  longBars: number;
};

export function normalizeRegimeCandleTimeframe(timeframe?: string | null): string {
  const key = String(timeframe ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
  return TF_ALIASES[key] ?? key;
}

/** Return (direction_short bars, direction long bars) for v3 — matches BE. */
export function directionLookbacksForTf(timeframe?: string | null): DirectionLookbacks {
  const key = normalizeRegimeCandleTimeframe(timeframe);
  const pair = DIRECTION_LOOKBACKS_BY_TF[key] ?? ([120, 480] as const);
  return { shortBars: pair[0], longBars: pair[1] };
}

export function directionBarsForHorizon(
  horizon: DirectionHorizon,
  timeframe?: string | null
): number {
  const { shortBars, longBars } = directionLookbacksForTf(timeframe);
  return horizon === "short" ? shortBars : longBars;
}

/** e.g. `~64 bars` */
export function formatDirectionBarsWindow(
  horizon: DirectionHorizon,
  timeframe?: string | null
): string {
  return `~${directionBarsForHorizon(horizon, timeframe)} bars`;
}

export function marketCandleDirectionLabel(
  horizon: DirectionHorizon,
  timeframe?: string | null
): string {
  return `Market candle direction (${formatDirectionBarsWindow(horizon, timeframe)})`;
}

export function marketCandleDirectionTitleCase(
  horizon: DirectionHorizon,
  timeframe?: string | null
): string {
  return `Market Candle Direction (${formatDirectionBarsWindow(horizon, timeframe)})`;
}

/**
 * Infer short vs long horizon from a `(~N bars)` label, using TF lookbacks when known.
 * Falls back to historical 120=short / 480=long when TF is unknown.
 */
export function directionHorizonFromBarsLabel(
  labelText: string,
  timeframe?: string | null
): DirectionHorizon | null {
  const match = String(labelText ?? "").match(/\(~\s*(\d+)\s*bars\)/i);
  if (!match) return null;
  const n = Number(match[1]);
  if (!Number.isFinite(n)) return null;
  const { shortBars, longBars } = directionLookbacksForTf(timeframe);
  if (n === shortBars && n !== longBars) return "short";
  if (n === longBars) return "long";
  if (n === shortBars) return "short";
  if (/short|recent\s+drift/i.test(labelText)) return "short";
  if (/long\s+drift|\blong\b/i.test(labelText) && !/short/i.test(labelText)) return "long";
  return null;
}
